import { useIntl } from 'react-intl';
import SummaryTable, { SummaryRow } from '../../table/SummaryTable';
import Time from '../../../../../../modules/components/time/Time';
import { KTSVG, toTitleCase } from '../../../../../../../_metronic/helpers';
import clsx from 'clsx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface Props {
  data: any;
  status: string;
  refetch: () => void;
}

export default function Detail({ data, status, refetch }: Props) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_DOMAIN.${id}` });
  const emDash = '—';

  const sslDataSection: SummaryRow[] = [
    {
      key: 'host_name',
      field: t('HOST_NAME'),
      render: () => data?.result?.security_info?.ssl_info?.host_name,
    },
    {
      key: 'ssl_validity_period',
      field: t('SSL_VALIDITY_PERIOD'),
      render: () =>
        data?.result?.security_info?.ssl_info?.ssl_validity_period &&
        Object.keys(data?.result?.security_info?.ssl_info?.ssl_validity_period).length > 0 && (
          <span
            className={
              data?.result?.security_info?.ssl_info?.ssl_validity_period?.years ? '' : 'text-danger'
            }
          >
            {!!data?.result?.security_info?.ssl_info?.ssl_validity_period?.years &&
              `${data?.result?.security_info?.ssl_info?.ssl_validity_period?.years} ${t('YEARS')} `}
            {!!data?.result?.security_info?.ssl_info?.ssl_validity_period?.months &&
              `${data?.result?.security_info?.ssl_info?.ssl_validity_period?.months} ${t('MONTHS')} `}
            {!!data?.result?.security_info?.ssl_info?.ssl_validity_period?.days &&
              `${data?.result?.security_info?.ssl_info?.ssl_validity_period?.days} ${t('DAYS')} `}
          </span>
        ),
    },
    {
      key: 'start_date',
      field: t('START_DATE'),
      render: () =>
        data?.result?.security_info?.ssl_info?.start_date && (
          <Time
            date={new Date(data?.result?.security_info?.ssl_info?.start_date)}
            format='yyyy-MM-dd HH:mm:ss'
          />
        ),
    },
    {
      key: 'expire_date',
      field: t('EXPIRY_DATE'),
      render: () =>
        data?.result?.security_info?.ssl_info?.expire_date && (
          <Time
            date={new Date(data?.result?.security_info?.ssl_info?.expire_date)}
            format='yyyy-MM-dd HH:mm:ss'
          />
        ),
    },
    {
      key: 'issuer.C',
      field: t('ISSUER_C_COUNTRY'),
      render: () => data?.result?.security_info?.ssl_info?.issuer?.C,
    },
    {
      key: 'issuer.O',
      field: t('ISSUER_O_ORGANIZATION'),
      render: () => data?.result?.security_info?.ssl_info?.issuer?.O,
    },
    {
      key: 'issuer.CN',
      field: t('ISSUER_CN_COMMON_NAME'),
      render: () => data?.result?.security_info?.ssl_info?.issuer?.CN,
    },
    {
      key: 'error',
      field: t('ERROR'),
      render: () => {
        const sslError = data?.result?.security_info?.ssl_info?.error;
        return (
          sslError && (
            <span className='text-danger'>
              {toTitleCase(sslError?.replaceAll('_', ' '))}
            </span>
          )
        );
      },
    },
  ].filter((row) => row.render());

  const isBooleanTrue = (value: boolean | null) => {
    return typeof value === 'boolean' && value;
  };
  const getSafetyInfoValue = (value: boolean | null) => {
    let label = '';
    let icon = null;
    if (isBooleanTrue(value)) {
      label = t('DETECTED');
      icon = (
        <KTSVG path='/media/icons/duotune/general/gen050.svg' className='svg-icon-1 text-danger' />
      );
    } else if (typeof value === 'boolean' && !value) {
      label = t('SAFE');
      icon = (
        <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-1 text-success' />
      );
    } else {
      label = t('UNKNOWN');
      icon = (
        <KTSVG path='/media/icons/duotune/general/gen042.svg' className='svg-icon-1 text-muted' />
      );
    }
    return (
      <OverlayTrigger placement='top' overlay={<Tooltip>{label}</Tooltip>}>
        <span>{icon}</span>
      </OverlayTrigger>
    );
  };
  const safetyInfoLabelMap = {
    malware: t('MALWARE'),
    phishing: t('PHISHING'),
    unwanted_software: t('UNWANTED_SOFTWARE'),
    potentially_harmful_application: t('POTENTIALLY_HARMFUL_APPLICATION'),
  };
  const safetyDataSection: Array<{
    key: string;
    field: string;
    value: React.ReactNode;
    isDanger?: boolean;
  }> = Object.entries(safetyInfoLabelMap).map(([key, label]) => ({
    key,
    field: label,
    value: getSafetyInfoValue(data?.result?.security_info?.safety_info?.[key]),
    isDanger: isBooleanTrue(data?.result?.security_info?.safety_info?.[key]),
  }));

  return (
    <div className='card mb-5 mb-xl-10 border'>
      <div className='card-header align-items-center justify-content-start'>
        <h4 className='my-6'>{t('SECURITY')}</h4>
      </div>
      <div className='card-body p-9 d-flex flex-column gap-9'>
        <div className='row g-5 g-md-6'>
          {safetyDataSection.map((item) => (
            <div className='col-6 col-lg-auto' key={item.key}>
              <div
                className={clsx(
                  'border border-gray-300 border-dashed rounded py-2 px-4 h-100',
                  item?.isDanger &&
                  'bg-light-danger border border-dashed border-danger shadow-sm text-danger'
                )}
              >
                <span>{item.field}</span> {item.value}
              </div>
            </div>
          ))}
        </div>
        <div>
          <h5 className='fw-bolder m-0'>{t('SSL_CERTIFICATE_CHECK')}</h5>
          <SummaryTable rows={sslDataSection} data={data} refetch={refetch} />
        </div>
      </div>
      <div className='d-flex justify-content-end mb-5'>
        <a href='#summary' className='px-8 d-none print-display-button'>
          <i className="bi bi-caret-up-square-fill fs-3"></i>
        </a>
      </div>
    </div>
  );
}
